import { StandardEvents } from '@shopify/events';

/**
 * <kr-product-gallery> — thumbnail rail, variant sync and an optional lightbox.
 *
 * The slides themselves are a <kr-slider>, so swiping, dots and scroll-snap already work
 * without this element. It adds three things:
 *   1. Thumbnails that drive the slider, and follow it back when the user swipes. The
 *      rail is a single-row horizontal scroller (CSS), so this element keeps the active
 *      thumb in view, tracks which edge is overflowing for the fade, and translates
 *      mouse drags into scrolling — the rail has no arrows by design.
 *   2. Variant sync: Horizon's variant picker dispatches a productSelect event whose
 *      promise resolves with the selected variant, so the gallery can jump to that
 *      variant's media.
 *   3. A minimal native <dialog> lightbox. Deliberately not Horizon's <zoom-dialog>,
 *      whose markup contract (drag-zoom-wrapper, thumbnail rail, view transitions) is far
 *      more than this design needs.
 */
class KrProductGallery extends HTMLElement {
  #section = null;
  #adapts = false;
  #railObserver = null;
  #railAbort = null;
  #dragAbort = null;
  #dragging = false;
  #overflowFrame = 0;

  connectedCallback() {
    this.slider = this.querySelector('kr-slider');
    this.thumbs = Array.from(this.querySelectorAll('[data-kr-thumb]'));
    this.slides = Array.from(this.querySelectorAll('[data-kr-slide]'));
    this.#adapts = this.hasAttribute('data-adapt');

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.select(index));
    });

    this.slider?.addEventListener('kr-slider:change', (event) => this.#markActive(event.detail.index));

    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

    this.#setupThumbRail();
    this.#setupLightbox();
    this.#markActive(0);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#railObserver?.disconnect();
    this.#railAbort?.abort();
    this.#dragAbort?.abort();
    cancelAnimationFrame(this.#overflowFrame);
  }

  /** @param {number} index */
  select(index) {
    this.slider?.goTo(index);
    this.#markActive(index);
  }

  #markActive(index) {
    this.#syncRatio(index);

    this.thumbs.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('kr-gallery__thumb--active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.#revealThumb(index);
  }

  /**
   * Adapt mode: the stage follows whichever slide is active, so a vertical shot gets a
   * vertical frame and a square one a square frame — no letterboxing, no crop.
   *
   * One shared ratio rather than one per slide, because every slide sits in the same
   * implicit grid row of `.kr-slider__viewport` and so shares its height regardless;
   * a per-slide ratio would only move the dead space, not remove it.
   */
  #syncRatio(index) {
    if (!this.#adapts) return;

    const ratio = this.slides[index]?.dataset.ratio;
    if (ratio) this.style.setProperty('--kr-gallery-ratio', ratio);
  }

  #onProductSelect = (event) => {
    event.promise
      ?.then(({ detail, variant }) => {
        // `resource` is the variant JSON the picker read back out of the refetched page.
        const mediaId = detail?.resource?.featured_media?.id ?? variant?.featuredMedia?.id;
        if (!mediaId) return;

        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index >= 0) this.select(index);
      })
      .catch(() => {
        /* Unavailable variant combinations resolve without media — nothing to sync. */
      });
  };

  /* --- Thumbnail rail ------------------------------------------------------ */

  #verticalRail() {
    return this.thumbRail && getComputedStyle(this.thumbRail).flexDirection === 'column';
  }

  #setupThumbRail() {
    this.thumbRail = this.querySelector('[data-kr-thumbs]');
    if (!this.thumbRail) return;

    const controller = new AbortController();
    this.#railAbort = controller;
    const { signal } = controller;

    this.thumbRail.addEventListener('scroll', this.#queueOverflowUpdate, { passive: true, signal });
    this.thumbRail.addEventListener('pointerdown', this.#onThumbPointerDown, { signal });

    // A ResizeObserver rather than a window listener: it also fires once on observe
    // (so the first measurement happens after layout, not from connectedCallback where
    // the thumbs may still be unsized) and catches thumbnail_size edits in the editor.
    this.#railObserver = new ResizeObserver(this.#queueOverflowUpdate);
    this.#railObserver.observe(this.thumbRail);
  }

  #queueOverflowUpdate = () => {
    cancelAnimationFrame(this.#overflowFrame);
    this.#overflowFrame = requestAnimationFrame(this.#updateThumbOverflow);
  };

  /** Records which edge has more thumbs behind it, so the CSS can fade just that edge. */
  #updateThumbOverflow = () => {
    const rail = this.thumbRail;
    if (!rail) return;

    const vertical = this.#verticalRail();
    const max = vertical ? rail.scrollHeight - rail.clientHeight : rail.scrollWidth - rail.clientWidth;
    // RTL scrolls into negative scrollLeft, so distance travelled from the rail's
    // start is the absolute value in both directions.
    const travelled = Math.abs(vertical ? rail.scrollTop : rail.scrollLeft);

    let state = 'none';
    if (max > 1) {
      const atStart = travelled <= 1;
      const atEnd = travelled >= max - 1;
      if (atStart && atEnd) state = 'none';
      else if (atStart) state = 'end';
      else if (atEnd) state = 'start';
      else state = 'both';
    }

    rail.dataset.overflow = state;
  };

  /** Scrolls the rail the minimum amount needed to bring a thumb fully into view. */
  #revealThumb(index) {
    const rail = this.thumbRail;
    const thumb = this.thumbs[index];
    if (!rail || !thumb) return;

    // Rect deltas rather than offsetLeft maths: they are physical in both LTR and RTL,
    // and scrollBy clamps itself at the ends. scrollIntoView is deliberately avoided —
    // it scrolls ancestors too, which would jerk the page inside the sticky gallery.
    const railRect = rail.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const margin = 8; // Matches the rail gap, and keeps the thumb clear of the fade.

    if (this.#verticalRail()) {
      let distance = 0;
      if (thumbRect.top < railRect.top + margin) distance = thumbRect.top - railRect.top - margin;
      else if (thumbRect.bottom > railRect.bottom - margin) distance = thumbRect.bottom - railRect.bottom + margin;
      if (distance) rail.scrollBy({ top: distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      return;
    }
    let delta = 0;
    if (thumbRect.left < railRect.left + margin) {
      delta = thumbRect.left - railRect.left - margin;
    } else if (thumbRect.right > railRect.right - margin) {
      delta = thumbRect.right - railRect.right + margin;
    }
    if (!delta) return;

    rail.scrollBy({
      left: delta,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  // The rail has no arrows by design, so a wheel-only mouse would have no way to reach
  // the thumbs past the edge. Touch and trackpads scroll natively and are left alone.
  #onThumbPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || this.#dragging) return;

    event.preventDefault();

    const rail = this.thumbRail;
    const vertical = this.#verticalRail();
    const startX = vertical ? event.clientY : event.clientX;
    const startScroll = vertical ? rail.scrollTop : rail.scrollLeft;
    let moved = false;

    const controller = new AbortController();
    const { signal } = controller;
    this.#dragAbort = controller;
    this.#dragging = true;

    // A drag ends with a click on whichever thumb is under the cursor; swallow it so
    // scrolling the rail never changes the selected media. Deliberately NOT tied to
    // `signal`: the click fires after pointerup, when the controller is already aborted.
    const suppressClick = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
    };

    const onPointerMove = (e) => {
      const delta = startX - (vertical ? e.clientY : e.clientX);
      if (!moved) {
        if (Math.abs(delta) < 3) return;
        moved = true;
        rail.setPointerCapture(e.pointerId);
        rail.dataset.dragging = '';
        document.addEventListener('click', suppressClick, { capture: true, once: true });
      }
      if (vertical) rail.scrollTop = startScroll + delta;
      else rail.scrollLeft = startScroll + delta;
    };

    const onPointerUp = () => {
      controller.abort();
      this.#dragAbort = null;
      this.#dragging = false;
      delete rail.dataset.dragging;

      // The drag's own click (if any) dispatches synchronously before this timeout, so
      // this only cleans up sessions that ended without one (pointercancel, release
      // outside the window) — otherwise the next unrelated click would be swallowed.
      if (moved) setTimeout(() => document.removeEventListener('click', suppressClick, true), 0);
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };

  /* --- Lightbox ----------------------------------------------------------- */

  #setupLightbox() {
    this.dialog = this.querySelector('[data-kr-lightbox]');
    if (!this.dialog) return;

    this.dialogImage = this.dialog.querySelector('[data-kr-lightbox-image]');
    this.sources = this.slides.map((slide) => {
      const img = slide.querySelector('img');
      return img ? { src: img.currentSrc || img.src, srcset: img.srcset, alt: img.alt } : null;
    });

    this.querySelectorAll('[data-kr-zoom]').forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.openLightbox(index));
    });

    this.dialog.querySelector('[data-kr-lightbox-close]')?.addEventListener('click', () => this.dialog.close());
    // Clicking the backdrop closes; the image itself sits in an inner element.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  /** @param {number} index */
  openLightbox(index) {
    const source = this.sources?.[index];
    if (!source || !this.dialogImage) return;

    this.dialogImage.src = source.src;
    this.dialogImage.srcset = source.srcset;
    this.dialogImage.alt = source.alt;
    this.dialog.showModal();
  }
}

/**
 * <kr-sticky-atc> — reveals a slim bar once the real Add to cart button scrolls away.
 *
 * The bar's button forwards its click to the real one, so there is exactly one add-to-cart
 * code path and no duplicated form or cart logic.
 */
class KrStickyAtc extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const targetSelector = this.dataset.target;
    const target = targetSelector ? document.querySelector(targetSelector) : null;
    if (!target) return;

    this.proxy = this.querySelector('[data-kr-sticky-submit]');
    this.proxy?.addEventListener('click', () => {
      const button = target.querySelector('button[type="submit"], button') ?? target;
      button.click();
    });

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.dataset.visible = entry.isIntersecting || entry.boundingClientRect.top > 0 ? 'false' : 'true';
      },
      { rootMargin: '0px 0px -100% 0px' }
    );
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('kr-product-gallery')) {
  customElements.define('kr-product-gallery', KrProductGallery);
}

if (!customElements.get('kr-sticky-atc')) {
  customElements.define('kr-sticky-atc', KrStickyAtc);
}
